import React, { useEffect, useState } from "react"
import { Link } from "gatsby"
import styled from "styled-components"
import { useLocation } from "@reach/router"

import {
  BorderRadius,
  Color,
  Spacing,
  Typography,
} from "@persona-id/design-tokens"
import { SearchIcon } from "@persona-id/design-tokens/icons/16px"
import Button from "@persona-id/design-system/components/Button"
import NavigationTopSearch from "./NavigationTopSearch"

import menuIcon from "../images/menu-icon.svg"
import personaLogo from "../images/persona-logo.svg"

const NavigationTop = ({
  toggleNavigationLeftMobile,
  closeNavigationLeftMobile,
}: {
  toggleNavigationLeftMobile: () => void
  closeNavigationLeftMobile: () => void
}) => {
  const location = useLocation()
  const hideNavigationTop =
    location.pathname.includes("end-users") &&
    !location.pathname.includes("for-end-users")

  const [isDesktopSearchActive, setIsDesktopSearchActive] = useState(false)
  const [isMobileSearchActive, setIsMobileSearchActive] = useState(false)

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey && e.key === "k") {
        if (!(process.env.IS_SEARCH_DISABLED === "true")) {
          if (window.innerWidth > 900) {
            setIsDesktopSearchActive(true)
          } else {
            setIsMobileSearchActive(true)
          }
        }
      }
    }
    window.addEventListener("keydown", onKeyDown)

    return () => {
      window.removeEventListener("keydown", onKeyDown)
    }
  }, [])

  return (
    <NavigationTopWrapper>
      {isMobileSearchActive ? (
        <NavigationTopSearch
          closeSearch={() => setIsMobileSearchActive(false)}
        />
      ) : (
        <>
          <NavigationTopLogo>
            <Link to="/">
              <img src={personaLogo} alt="Persona logo" />
              Persona Help Center
            </Link>
          </NavigationTopLogo>
          {!hideNavigationTop && !isDesktopSearchActive && (
            <NavigationTopLinks>
              <Link to="/articles">Articles</Link>
              <Link to="/community">Community</Link>
              {!(process.env.IS_ACADEMY_DISABLED === "true") && (
                <Link to="/academy">Academy</Link>
              )}
              <a
                href="https://docs.withpersona.com/docs"
                target="_blank"
                rel="noopener noreferrer"
              >
                Developer Docs
              </a>
              <a
                href="https://status.withpersona.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                System Status
              </a>
              {!(process.env.IS_SEARCH_DISABLED === "true") && (
                <SearchIcon onClick={() => setIsDesktopSearchActive(true)} />
              )}
            </NavigationTopLinks>
          )}
          {isDesktopSearchActive && (
            <NavigationTopSearch
              closeSearch={() => setIsDesktopSearchActive(false)}
            />
          )}
          {!hideNavigationTop && (
            <NavigationTopRight>
              <a
                href={`${process.env.PERSONA_HOSTNAME}/dashboard`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button
                  id="navigation-top__dashboard"
                  aria-label="navigation-top__dashboard"
                  variant="primary"
                  size="md"
                >
                  Go to Dashboard
                </Button>
              </a>
              {!(process.env.IS_SEARCH_DISABLED === "true") && (
                <SearchIcon
                  onClick={() => {
                    setIsMobileSearchActive(true)
                    closeNavigationLeftMobile()
                  }}
                />
              )}
              <img
                src={menuIcon}
                alt="Menu icon"
                onClick={() => toggleNavigationLeftMobile()}
              />
            </NavigationTopRight>
          )}
        </>
      )}
    </NavigationTopWrapper>
  )
}

const NavigationTopWrapper = styled.div`
  display: flex;
  flex-direction: row;
  padding: ${Spacing[4]} ${Spacing[6]};
  justify-content: space-between;
  align-items: center;
  background-color: ${Color.PrimaryWhite};
  box-shadow: 0px 1px 0px ${Color.BorderHairline};
  ${Typography.TextMedium};

  @media (max-width: 900px) {
    padding: ${Spacing[3]} ${Spacing[2]};
  }

  a {
    color: ${Color.TextPrimary};
    text-decoration: none;
  }
`

const NavigationTopLogo = styled.div`
  a {
    display: flex;
    flex-direction: row;
    align-items: center;
    column-gap: ${Spacing[3]};
  }
`

const NavigationTopLinks = styled.div`
  display: flex;
  flex-direction: row;
  column-gap: ${Spacing[3]};
  align-items: center;
  margin: 0px ${Spacing[4]};

  a {
    color: ${Color.TextPrimary};
    text-decoration: none;
    padding: ${Spacing[1]} ${Spacing[2]};
    border-radius: ${BorderRadius.Sm};
  }

  a:hover {
    background-color: ${Color.BackgroundButtonDefault};
  }

  svg {
    cursor: pointer;
  }

  @media (max-width: 900px) {
    display: none;
  }
`

const NavigationTopRight = styled.div`
  display: flex;
  flex-direction: row;
  column-gap: ${Spacing[2]};
  align-items: center;

  img,
  svg {
    display: none;
    cursor: pointer;

    @media (max-width: 900px) {
      display: block;
    }
  }

  button {
    @media (max-width: 900px) {
      display: none;
    }
  }
`

export default NavigationTop
